import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchNCSubtypeById,
  createNCSubtype,
  updateNCSubtype,
  fetchNCTypes,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSuccessMessage,
  clearSuccessMessage,
} from '../../store/reducers/ncsubtypeSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  formCard: {
    marginBottom: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  actionsSection: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  activeStatus: {
    color: theme.palette.success.main,
  },
  inactiveStatus: {
    color: theme.palette.error.main,
  },
}));

const NCSubtypeEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditing = Boolean(id);
  const ncsubtypeId = id ? parseInt(id, 10) : null;

  const {
    selectedItem,
    formData,
    loading,
    error,
    validationErrors,
    successMessage,
    ncTypes,
    ncTypesLoading,
  } = useSelector((state: RootState) => state.ncsubtype);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load data for editing and NC Types
  useEffect(() => {
    dispatch(fetchNCTypes());
    
    if (isEditing && ncsubtypeId) {
      dispatch(fetchNCSubtypeById(ncsubtypeId));
    } else {
      // Reset form for new item
      dispatch(resetFormData());
    }
  }, [dispatch, isEditing, ncsubtypeId]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Update form data when selected item changes (for editing)
  useEffect(() => {
    if (selectedItem && isEditing) {
      dispatch(setFormData({
        Name: selectedItem.Name,
        Description: selectedItem.Description || '',
        Active: selectedItem.Active,
        NCTypeId: selectedItem.NCTypeId,
      }));
    }
  }, [selectedItem, isEditing, dispatch]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    dispatch(setFormData({ [field]: value }));
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(clearValidationErrors());
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      if (isEditing && ncsubtypeId) {
        await dispatch(updateNCSubtype(ncsubtypeId, formData));
      } else {
        await dispatch(createNCSubtype(formData));
      }
      
      // Navigate back to list on success
      setTimeout(() => {
        navigate('/catalogs/ncsubtype');
      }, 2000);
    } catch (error) {
      console.error('Error saving NC Subtype:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/catalogs/ncsubtype');
  };

  const handleBack = () => {
    navigate('/catalogs/ncsubtype');
  };

  if (loading && isEditing) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" mb={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            style={{ marginRight: 16 }}
          >
            Back to List
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit NC Subtype' : 'Add New NC Subtype'}
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="textSecondary">
          {isEditing 
            ? 'Update the details of this Non-Conformance Subtype'
            : 'Create a new Non-Conformance Subtype'
          }
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {successMessage}
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Form */}
      <Paper className={classes.formCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subtype Details
          </Typography>
          
          <Grid container spacing={3}>
            {/* NC Type Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth className={classes.formField} required>
                <InputLabel>NC Type</InputLabel>
                <Select
                  value={formData.NCTypeId || ''}
                  onChange={(e) => handleInputChange('NCTypeId', e.target.value)}
                  label="NC Type"
                  error={validationErrors.some(error => error.includes('NC Type'))}
                >
                  <MenuItem value="">
                    <em>Select an NC Type</em>
                  </MenuItem>
                  {ncTypes.map((ncType) => (
                    <MenuItem key={ncType.Id} value={ncType.Id}>
                      {ncType.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Subtype Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Subtype Name"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                variant="outlined"
                required
                className={classes.formField}
                error={validationErrors.some(error => error.includes('Subtype Name'))}
                helperText={validationErrors.find(error => error.includes('Subtype Name'))}
              />
            </Grid>

            {/* Active Status */}
            <Grid item xs={12} md={6}>
              <Box className={classes.statusIndicator}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.Active}
                      onChange={(e) => handleInputChange('Active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Active"
                />
                <Typography
                  variant="body2"
                  className={formData.Active ? classes.activeStatus : classes.inactiveStatus}
                >
                  {formData.Active ? 'This subtype is currently active' : 'This subtype is currently inactive'}
                </Typography>
              </Box>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.Description}
                onChange={(e) => handleInputChange('Description', e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                className={classes.formField}
                error={validationErrors.some(error => error.includes('Description'))}
                helperText={
                  validationErrors.find(error => error.includes('Description')) ||
                  'Optional description for this NC Subtype (max 500 characters)'
                }
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
          </Grid>

          {/* Additional Information for Editing */}
          {isEditing && selectedItem && (
            <>
              <Divider style={{ margin: '24px 0' }} />
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>ID:</strong> {selectedItem.Id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Created:</strong> {selectedItem.CreatedDate 
                      ? new Date(selectedItem.CreatedDate).toLocaleDateString()
                      : 'Not available'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Modified:</strong> {selectedItem.ModifiedDate
                      ? new Date(selectedItem.ModifiedDate).toLocaleDateString()
                      : 'Not available'
                    }
                  </Typography>
                </Grid>
                {selectedItem.NCType && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Parent NC Type:</strong> {selectedItem.NCType.Name}
                      {selectedItem.NCType.Description && ` - ${selectedItem.NCType.Description}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </CardContent>
      </Paper>

      {/* Actions */}
      <Box className={classes.actionsSection}>
        <Box className={classes.actionButtons}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting || loading || ncTypesLoading}
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Subtype' : 'Create Subtype')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NCSubtypeEdit; 